// src/components/shell/DirtyFormPromptModal.tsx
//
// Visual modal for the unsaved-changes guard. The provider in
// `DirtyFormPromptContext` owns the open/closed state + the action
// callbacks; this component just renders the three-button prompt
// when state is open.

import { useEffect, useState } from "react";
import type { DirtyFormEntry } from "@/contexts/DirtyFormPromptContext";

export type DirtyFormPromptModalProps = {
  open: boolean;
  dirtyEntries: DirtyFormEntry[];
  onCancel: () => void;
  onDiscard: () => void;
  onSaveAndContinue: () => Promise<void>;
};

export function DirtyFormPromptModal(props: DirtyFormPromptModalProps) {
  const { open, dirtyEntries, onCancel, onDiscard, onSaveAndContinue } = props;
  const [saving, setSaving] = useState(false);

  // Esc closes (treated as Cancel). Bound only while open so we don't
  // intercept keystrokes elsewhere.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const hasSaveablePanel = dirtyEntries.some((e) => typeof e.save === "function");
  const labels = dirtyEntries.map((e) => e.label);
  const summary =
    labels.length === 1
      ? labels[0]
      : labels.length === 2
        ? `${labels[0]} and ${labels[1]}`
        : `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;

  async function handleSave() {
    setSaving(true);
    try {
      await onSaveAndContinue();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-zinc-900">
        <h3 className="text-base font-bold text-gray-900 dark:text-zinc-100">
          Unsaved changes
        </h3>
        <p className="mt-1 text-sm text-gray-700 dark:text-zinc-300">
          You have unsaved changes in {summary}. Save before leaving, or discard
          to navigate away without keeping them.
        </p>

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDiscard}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg border border-red-300 text-sm text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-950/40 disabled:opacity-50"
          >
            Discard changes
          </button>
          {hasSaveablePanel ? (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save & continue"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
